import React, { useState } from "react";
import { toast } from "sonner";
import { Printer, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Shared "Request a printer reprint" button — appears on the Stickers,
 * Vehicles and Settings pages. Opens a small confirm dialog collecting
 * an optional reason + quantity, then starts a Stripe Checkout for the
 * flat $50 reprint fee. On payment success the backend notifies
 * support@scanmint.app and adds a row to the SuperAdmin
 * Admin Notifications inbox.
 *
 * Props:
 *   propertyId — required
 *   variant    — "primary" (green solid) | "outline" | "ghost"
 *   size       — "sm" | "default" | "lg"
 *   className  — extra tailwind
 *   testId     — data-testid override, otherwise `sticker-reprint-request`
 */
export function StickerReprintButton({
  propertyId,
  variant = "primary",
  size = "default",
  className = "",
  testId = "sticker-reprint-request",
  label = "Request printer reprint",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState("");

  const start = async () => {
    if (!propertyId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/stickers/reprint-checkout", {
        property_id: propertyId,
        quantity,
        reason,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout");
        setBusy(false);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const btnClass =
    variant === "primary"
      ? "bg-[#0F8F4F] hover:bg-[#0A6B3B] text-white"
      : "";

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant={variant === "primary" ? "default" : variant}
        size={size}
        className={`${btnClass} ${className}`}
        data-testid={testId}
      >
        <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="sticker-reprint-dialog">
          <DialogHeader>
            <DialogTitle>Request a printer reprint · $50</DialogTitle>
            <DialogDescription>
              Send a fresh sticker batch to the printer. You&rsquo;ll be charged
              a flat <b>$50</b> at Stripe checkout. Our team is notified the
              moment payment settles and will ship within 3&ndash;5 business days.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="mono-tiny text-[#71717A]">
                How many packs (25 stickers each)?
              </Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                data-testid="sticker-reprint-quantity"
              />
            </div>

            <div className="space-y-2">
              <Label className="mono-tiny text-[#71717A]">
                Reason (optional — helps the print team)
              </Label>
              <Input
                placeholder="e.g. stickers faded, updated branding, expanded property"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="sticker-reprint-reason"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid="sticker-reprint-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={start}
              disabled={busy}
              className="bg-[#0F8F4F] hover:bg-[#0A6B3B]"
              data-testid="sticker-reprint-confirm"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Opening Stripe…
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Pay $50 & submit
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
